import axios from '@/libs/axiosInstance';
import {
  clearPKCEParams,
  retrievePKCEParams,
  storePKCEParams,
  verifyState,
} from '@/libs/pkce-utils';
import {
  exchangeAuthorizationCode,
  finalizeInstagramConnection,
  getOAuthRedirect,
  type CodeExchangeRequest,
  type InstagramPage,
} from '@/services/instagram-service';
import { create } from 'zustand';

/** OAuth flow only: whether the integration is live is the server's answer, read from the integrations query. */
interface InstagramState {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  disconnect: (id: string) => Promise<void>;
  initiateOAuthFlow: () => Promise<void>;
  exchangeCodeForToken: (
    code: string,
    state: string,
    brandId: string
  ) => Promise<{ pages: InstagramPage[]; tokenInfo: any }>;
  finalizeConnection: (page: InstagramPage, brandId: string) => Promise<void>;
}

export const useInstagramStore = create<InstagramState>((set) => ({
  isLoading: false,

  setLoading: (loading) => set({ isLoading: loading }),

  disconnect: async (id) => {
    set({ isLoading: true });
    try {
      await axios.delete('/dashboard/integrations/' + id);
      // Clear any stored PKCE parameters on disconnect
      clearPKCEParams();
    } catch (error) {
      console.error('Failed to disconnect Instagram:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Initiate OAuth flow using authorization code flow with PKCE
   */
  initiateOAuthFlow: async () => {
    set({ isLoading: true });
    try {
      // Get OAuth redirect URL and PKCE parameters from backend
      const oauthResponse = await getOAuthRedirect();

      // Store PKCE parameters in session storage for later use
      storePKCEParams({
        codeVerifier: oauthResponse.codeVerifier,
        state: oauthResponse.state,
      });

      // Redirect to Facebook OAuth URL
      window.location.href = oauthResponse.redirectUrl;
    } catch (error) {
      console.error('Failed to initiate OAuth flow:', error);
      set({ isLoading: false });
      throw error;
    }
    // Note: Loading state is not reset here as the page will redirect
  },

  /**
   * Exchange authorization code for access token using stored PKCE parameters
   */
  exchangeCodeForToken: async (code: string, state: string, brandId: string) => {
    set({ isLoading: true });
    try {
      // Verify state parameter to prevent CSRF attacks
      if (!verifyState(state)) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }

      // Retrieve stored PKCE parameters
      const storedParams = retrievePKCEParams();
      if (!storedParams) {
        throw new Error('No PKCE parameters found in session storage');
      }

      // Prepare code exchange request
      const exchangeRequest: CodeExchangeRequest = {
        code,
        codeVerifier: storedParams.codeVerifier,
        state: storedParams.state,
        brandId,
      };

      // Exchange authorization code for access token
      const response = await exchangeAuthorizationCode(exchangeRequest);

      // Clear PKCE parameters after successful exchange
      clearPKCEParams();

      return {
        pages: response.pages || [],
        tokenInfo: response.tokenInfo,
      };
    } catch (error) {
      console.error('Failed to exchange authorization code:', error);
      // Clear PKCE parameters on error to prevent replay attacks
      clearPKCEParams();
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Finalize Instagram connection with selected page
   */
  finalizeConnection: async (page: InstagramPage, brandId: string) => {
    set({ isLoading: true });
    try {
      await finalizeInstagramConnection(page, brandId);
    } catch (error) {
      console.error('Failed to finalize Instagram connection:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
}));
